import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import noop from 'lodash/noop';
import { ProfileSettingsConfig } from '@Constants/rx-user/constants';
import ProfileSettings from '@Services/ProfileSettings';
import { isIktosPredictiveRetrosynthesisEnabled, isPaiPredictiveRetrosynthesisEnabled } from '@Pages/RetroSynthesis/predictiveUtils';

import './PreferencesSynthesisPlannerSection.scss';

import PreferencesSectionItem from '../../PreferencesSectionItem';
import BlockHeading from '../../BlockHeading';
import SynthesisConditionsPosition from './Components/SynthesisConditionsPosition';
import ShowAutoplan from './Components/ShowAutoplan';
import SynthesisPlannerPredictedPai from './Components/SynthesisPlannerPredictedPai';
import SynthesisPlannerPredictedIktos from './Components/SynthesisPlannerPredictedIktos';
import SynthesisPlannerPublished from './Components/SynthesisPlannerPublished';

const getConditionsDisplayOptions = () => {
  const combinedInfo = ProfileSettings.getCombinedInfo();
  return ProfileSettings.getModelValue(combinedInfo, `SYNTHESIS_PLANNER.RX2_SYNTHESIS_CONDITIONS_POSITION`).values;
};

const getConditionsDisplayOptionTitle = (editUser) => {
  const options = getConditionsDisplayOptions();
  const defaultValue = ProfileSettings.parseDefaultValue(options);
  const currentValue = get(editUser, 'SYNTHESIS_PLANNER.RX2_SYNTHESIS_CONDITIONS_POSITION', defaultValue);
  const currentOption = isArray(options) ? options.find((option) => option.value === currentValue) : {};
  return get(currentOption, 'label', '');
};

function PreferencesSynthesisPlannerSection({ editUser, onChange, onSave, onClose, onReset }) {
  const isPredictedIktos = isIktosPredictiveRetrosynthesisEnabled();
  const isPredictedPai = isPaiPredictiveRetrosynthesisEnabled() && !isPredictedIktos;
  return (
    <div data-e2e="profile-settings-preferences-section-synthesis-planner">
      <BlockHeading title={ProfileSettingsConfig.Labels.synthesisPlanner} />
      <PreferencesSectionItem
        name="AUTOPLAN"
        prefix="AUTOPLAN"
        preferences={editUser.AUTOPLAN}
        options={ProfileSettings.getCombinedInfo().AUTOPLAN.preferences}
        onChange={onChange}
        onClose={onClose}
        onSave={onSave}
        onReset={onReset}>
        <SynthesisPlannerPublished />
      </PreferencesSectionItem>

      {isPredictedPai && (
        <PreferencesSectionItem
          name="PREDICTION_PLAN"
          prefix="PREDICTION_PLAN"
          preferences={editUser.PREDICTION_PLAN}
          options={ProfileSettings.getCombinedInfo().PREDICTION_PLAN.preferences}
          editUser={editUser}
          onChange={onChange}
          onClose={onClose}
          onSave={onSave}
          onReset={onReset}>
          <SynthesisPlannerPredictedPai />
        </PreferencesSectionItem>
      )}

      {isPredictedIktos && (
        <PreferencesSectionItem
          name="PREDICTION_PLAN_IKTOS"
          prefix="PREDICTION_PLAN_IKTOS"
          preferences={editUser.PREDICTION_PLAN_IKTOS}
          options={ProfileSettings.getCombinedInfo().PREDICTION_PLAN_IKTOS.preferences}
          editUser={editUser}
          onChange={onChange}
          onClose={onClose}
          onSave={onSave}
          onReset={onReset}>
          <SynthesisPlannerPredictedIktos />
        </PreferencesSectionItem>
      )}

      <PreferencesSectionItem
        name="SHOW_AUTOPLAN"
        prefix="AUTOPLAN"
        currentOption={editUser.AUTOPLAN.RX2_SHOW_AUTOPLAN_PREF}
        onChange={onChange}
        onClose={onClose}
        onSave={onSave}
        onReset={onReset}>
        <ShowAutoplan />
      </PreferencesSectionItem>

      <PreferencesSectionItem
        name="RX2_SYNTHESIS_CONDITIONS_POSITION"
        prefix="SYNTHESIS_PLANNER"
        preferences={editUser.SYNTHESIS_PLANNER}
        conditionsDisplayOptionTitle={getConditionsDisplayOptionTitle(editUser)}
        conditionsDisplayOptions={getConditionsDisplayOptions()}
        onChange={onChange}
        onClose={onClose}
        onSave={onSave}
        onReset={onReset}>
        <SynthesisConditionsPosition />
      </PreferencesSectionItem>
    </div>
  );
}

PreferencesSynthesisPlannerSection.propTypes = {
  editUser: PropTypes.object,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  onReset: PropTypes.func,
};

PreferencesSynthesisPlannerSection.defaultProps = {
  onChange: noop,
  onSave: noop,
  onClose: noop,
  onReset: noop,
};

export default PreferencesSynthesisPlannerSection;
